<script>
import appConfig from '@/app.config'
import Layout from '@/router/layouts/main'
import VideoHistoryListV2 from './VideoHistoryListV2'
import VideoHistoryPlayerV2 from './VideoHistoryPlayerV2'
import McButtonBack from '@/components/McButtonBack'
import McButton from '@/components/McButton'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import { mapGetters, mapState } from 'vuex'
import { GET_VEHICLE_VIDEO_TRACKERS_VIDEO_HISTORY } from '../../../services/api/trackvideo/video'
import WorkerVideoHistory from 'worker-loader!./lib/worker.js'

export default {
  name: 'VideoHistoryV2',
  page: {
    title: 'Riwayat Video',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    McButtonBack,
    McButton,
    VideoHistoryPlayerV2,
    VideoHistoryListV2,
  },
  mixins: [Helper, MixPanel],
  data() {
    return {
      intervalUpdateDataFromWs: null,
      workerVideoHistory: null,
      bannerCloseClicked: false,
      wsMessageHandler: null,
    }
  },
  computed: {
    ...mapGetters('vehicle', [
      'vehicleDetail',
      'tempVehicleStatusByVehicleId',
      'vehicleStatusByVehicleId',
    ]),
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    ...mapState('vehicle', ['listVehicle']),
    isVideoDemo() {
      if (!this.$route.query?.vehicleId) {
        return false
      }
      const dataDemoVideoHistory = this.getFeatureFlagValue('vsms2_trackvision2_playback_demo_id')
      return Object.keys(dataDemoVideoHistory).includes(this.$route.query?.vehicleId.toString())
    },
    isVideoStatusOnline() {
      const vehicleId = parseInt(this.$route.query?.vehicleId)
      if (!vehicleId || this.listVehicle.length === 0) return false

      const vehicleStatus = this.getVehicleStatus

      if (this.isVideoDemo) return true
      if (!vehicleStatus) return false

      const secondEngineOn = this.getFeatureFlagValue('trackvision_video_offline_acc_on') || 60,
        secondEngineOff = this.getFeatureFlagValue('trackvision_video_offline_acc_off') || 600,
        lastReceive = vehicleStatus.lastReceive,
        engineOn = vehicleStatus.engineOn

      return !this.isDeviceTrackvisionOffline(
        secondEngineOn,
        secondEngineOff,
        lastReceive,
        engineOn
      )
    },
    getVehicleStatus() {
      const vehicleId = parseInt(this.$route.query?.vehicleId)
      if (!vehicleId) return null
      const vehicleStatus = this.vehicleStatusByVehicleId(vehicleId)
      if (!vehicleStatus) return null
      if (this.isVideoDemo) {
        return {
          ...vehicleStatus,
          calculatedMotionStatus: 'M',
        }
      }
      return vehicleStatus
    },
    isVideoOffline() {
      if (this.$store.state.vehicle.loadingVideoStatus) return false
      return !this.isVideoStatusOnline
    },
    showBannerOffline() {
      return (
        (this.hasAccess(['b3j-trackvision-video-history-status:read']) &&
          this.isVideoOffline &&
          !this.bannerCloseClicked &&
          this.$route.query?.vehicleId &&
          !this.isHasAccessSwitchView) ||
        (this.isVideoOffline &&
          !this.bannerCloseClicked &&
          this.$route.query?.vehicleId &&
          this.isHasAccessSwitchView &&
          this.isNewView)
      )
    },
    isHasAccessSwitchView() {
      return this.hasAccess(['b3n-trackvision-toggle-video-status:read'])
    },
    isNewView() {
      const isNewView = JSON.parse(localStorage.getItem('live-streaming-switch-view-on')) ?? true
      return isNewView
    },
    vehicleIds() {
      return this.listVehicle.map((item) => item.vehicleId)
    },
  },
  watch: {
    'vehicleDetail.licensePlate': {
      handler() {
        if (this.vehicleDetail.licensePlate && this.$route.params.entryPoint) {
          this.track('Visit Video History Page', {
            license_plate: this.vehicleDetail.licensePlate,
            entry_point: this.$route.params.entryPoint,
          })
        }
      },
      deep: true,
    },
  },
  created() {
    const vehicleId = Number(this.$route.query.vehicleId)
    if (vehicleId) {
      this.$store.commit('vehicle/setVehicleId', vehicleId)
    } else {
      this.$store.commit('vehicle/setVehicleId', null)
      this.track('Open Trackvision Video History')
    }
  },
  async mounted() {
    // TODO: is this needed ?
    // this.$store.commit('vehicle/clearPeriod')
    // ENDTODO
    this.$store.commit('vehicle/clearVideoStates')
    this.$store.commit('vehicle/clearPickVideo')
    this.$store.commit('vehicle/setVideoStatusDetail', 'noVideo')
    this.$store.commit('vehicle/setSource', '')
    this.$store.commit('vehicle/clearStatusText')
    this.$store.commit('vehicle/setPlaybackUidActive', null)
    this.$store.commit('vehicle/setLoadingVideoStatus', true)
    this.$store.commit('vehicle/setChannelActive', null)

    // Load script for VSS Howen
    const src = ['common.js', 'webgl.js', 'vss-player.js', 'pcm-player.js']

    const promises = []
    for (let i = 0; i < src.length; i++) {
      promises.push(this.loadScript(src[i]))
    }
    await Promise.all(promises)

    await this.loadDataVehicleVideoTrackers()

    if (
      (this.hasAccess(['b3j-trackvision-video-history-status:read']) &&
        !this.isHasAccessSwitchView) ||
      (this.isHasAccessSwitchView && this.isNewView)
    ) {
      await this.setupWebsocket()
    }
    await this.setDataVideoStatus()
  },

  beforeDestroy() {
    if (this.wsMessageHandler) {
      this.$store.dispatch('ws/unregisterMessageHandler', this.wsMessageHandler)
    }
  },

  async destroyed() {
    // Terminate the worker when the component is destroyed
    if (this.workerVideoHistory) {
      this.workerVideoHistory.terminate()
    }
    clearInterval(this.intervalUpdateDataFromWs)
  },

  methods: {
    async setDataVideoStatus() {
      await this.setVehicleStatuses()
      this.initWorker()
      await this.$nextTick()
      if (
        (this.hasAccess(['b3j-trackvision-video-history-status:read']) &&
          !this.isHasAccessSwitchView) ||
        (this.isHasAccessSwitchView && this.isNewView)
      ) {
        this.intervalUpdateDataFromWs = setInterval(() => {
          this.$store.commit('vehicle/setLoadingVideoStatus', false)
          if (this.$store.state.ws.socket.isConnected && this.workerVideoHistory) {
            this.workerVideoHistory.postMessage({
              action: 'calculatedVehicleStatuses',
              tempData: JSON.stringify(this.$store.state.vehicle.tempVehicleStatuses),
              data: JSON.stringify(this.$store.state.vehicle.vehicleStatuses),
            })
          }
        }, 4000)
      }
      this.$forceUpdate()
    },
    async loadDataVehicleVideoTrackers() {
      try {
        const response = await this.$apollo.query({
          query: GET_VEHICLE_VIDEO_TRACKERS_VIDEO_HISTORY,
          variables: {
            filter: {
              motionStatus: this.hasAccess(['assisted_motion_status:read'])
                ? ['assisted', 'idle', 'moving', 'offline', 'parking', 'unknown']
                : undefined,
            },
          },
          fetchPolicy: 'no-cache',
        })
        const dataResponse = response.data.vehicleVideoTrackers.vehicleVideoTrackers
        const vGroups = []
        const vehicleType = []
        dataResponse.forEach((data) => {
          const groups = data.vehicle.vehicle2Vgroup
          groups.forEach((group) => {
            if (!vGroups.some((g) => g.vgroupId === group.vgroupId)) {
              vGroups.push(group)
            }
          })
          if (
            !vehicleType.some((vt) => vt.vehicleTypeId === data.vehicle.vehicleType.vehicleTypeId)
          ) {
            vehicleType.push(data.vehicle.vehicleType)
          }
        })
        this.$store.commit('vehicle/setListVgroups', vGroups)
        this.$store.commit('vehicle/setListVehicleType', vehicleType)
        this.$store.commit(
          'vehicle/setListVehicle',
          dataResponse.map(
            ({ vehicleId, vehicle, lastReceive, calculatedMotionStatus, engineOn }) => {
              return {
                vehicleId,
                deviceCode: vehicle.device.deviceCode,
                lastReceive,
                calculatedMotionStatus,
                engineOn,
              }
            }
          )
        )
      } catch (e) {
        console.error(e)
      }
    },
    handleWebSocketMessage(message) {
      const signal = typeof message === 'string' ? JSON.parse(message) : message
      if (signal.type.startsWith('gps-')) {
        const vehicleId = Number(signal.type.slice(4))
        if (!this.vehicleIds.includes(vehicleId)) return
        this.calculatedTempWsData(vehicleId, signal.payload)
      }
    },

    async ensureWebSocketConnected() {
      try {
        const wsToken = localStorage.getItem('wsToken')
        const payload = localStorage.getItem('payload')
        if (!wsToken) return

        // Use centralized WebSocket connection
        // ws/WSConnect already handles all connection logic and waiting
        if (!this.$store.state.ws.socket.isConnected) {
          const wsUrl = `${process.env.VUE_APP_WS_URL}/ws?token=${wsToken}`
          // WSConnect returns a promise that resolves when connected
          // It handles race conditions and waits for ongoing connections automatically
          await this.$store.dispatch('ws/WSConnect', wsUrl)
        }

        // Send payload if connected and not already initialized
        // Only send if App.vue hasn't already sent the initial payload
        if (
          this.$store.state.ws.socket.isConnected &&
          payload &&
          !this.$store.state.ws.isInitialized
        ) {
          this.$store.dispatch('ws/updateTags', payload)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to ensure WebSocket connection:', error)
      }
    },

    async setupWebsocket() {
      this.wsMessageHandler = this.handleWebSocketMessage.bind(this)
      await this.$store.dispatch('ws/registerMessageHandler', this.wsMessageHandler)

      // Ensure websocket is connected and send payload
      await this.ensureWebSocketConnected()
    },

    async setVehicleStatuses() {
      try {
        const vehicles = this.listVehicle
        let tempVehicles = {}
        vehicles.forEach((data) => {
          tempVehicles[data.vehicleId] = {
            calculatedMotionStatus: data.calculatedMotionStatus,
            engineOn: data.engineOn,
            lastReceive: data.lastReceive,
          }
        })
        this.$store.commit('vehicle/setVehicleStatuses', tempVehicles)
      } catch (e) {
        console.error(e)
      }
    },

    goToVideoHistory() {
      this.track('Open Video Download History')
      this.$router.push({
        name: 'Video History Trackvision Download',
        query: {
          prevPage: 'video-history',
          videoHistoryVehicleId: this.$route.query.vehicleId,
          ...(this.isVideoDemo ? { isDemo: true } : {}),
        },
      })
    },
    loadScript(filename) {
      return new Promise((resolve, reject) => {
        // For scripts in public folder, use the relative path
        const src = `/vss/${filename}` // or process.env.BASE_URL + filename

        const script = document.createElement('script')
        script.src = src
        script.async = true

        script.onload = resolve
        script.onerror = reject

        document.head.appendChild(script)
      })
    },
    async initWorker() {
      if (typeof Worker !== 'undefined') {
        this.workerVideoHistory = new WorkerVideoHistory()
        await this.$nextTick()
        this.workerVideoHistory.onmessage = async (event) => {
          const { action, result } = event.data
          if (action === 'updateVehicleStatuses') {
            this.$store.commit('vehicle/setVehicleStatuses', JSON.parse(result))
            await this.$nextTick()
            this.$store.commit('vehicle/resetTempVehicleStatuses')
            this.$forceUpdate()
          }
        }
      }
    },
    calculatedTempWsData(vehicleId, data) {
      try {
        let calculatedMotionStatus = data.calculatedMotionStatus

        if (
          data.calculatedMotionStatus === 'A' &&
          !this.hasAccess(['assisted_motion_status:read'])
        ) {
          calculatedMotionStatus = 'P'
        }

        this.$store.commit('vehicle/setTempVehicleStatusesByVehicleId', {
          vehicleId,
          data: {
            ...this.tempVehicleStatusByVehicleId(vehicleId),
            lastReceive: data.lastReceive,
            latitude: data.latitude,
            longitude: data.longitude,
            calculatedMotionStatus,
            engineOn: data.engineOn,
          },
        })
      } catch (err) {
        console.error(err)
      }
    },
    handleClickRefresh() {
      window.location.reload()
    },
  },
}
</script>

<template>
  <Layout class="mc-trackvision__video-history">
    <div v-if="showBannerOffline" class="mc-videohistory__banner-video-status-offline">
      <div class="mc-videohistory__banner-left-content">
        <i class="mc-icon icon-outline_slash" />
        <span>
          {{ $t('videoHistory.banner.videoOffline') }}
        </span>
      </div>
      <div class="mc-videohistory__banner-right-content">
        <span class="mc-videohistory__banner-offline-refresh" @click="handleClickRefresh">
          {{ 'Refresh' }}
        </span>
        <i class="mc-icon icon-outline_close" @click="bannerCloseClicked = true" />
      </div>
    </div>
    <div class="mc-videohistory-page-top-container">
      <div>
        <h3 class="mc-general__label">
          {{ $t('videoHistory.pageTitle') }}
        </h3>
        <p class="mb-0">
          {{ $t('videoHistory.pageDesc') }}
        </p>
      </div>
      <div class="d-flex gap-2">
        <McButtonBack :href="`/live-streaming`" />
        <McButton
          variant="outline-primary"
          :text="$t('trackVideo.historyVideoDownload')"
          icon="icon-outline_file-text"
          @click="goToVideoHistory"
        />
      </div>
    </div>
    <b-container fluid class="mc-videohistory mc-videohistory-3">
      <div class="mc-videhistory-row--container">
        <VideoHistoryPlayerV2 />
        <VideoHistoryListV2 />
      </div>
    </b-container>
  </Layout>
</template>
